import React, { FC, ReactNode } from 'react';

import classNames from 'classnames';

import { Button } from '../UI/Button';
import {
  ButtonLinkProps,
  IButtonProps,
  NativeButtonProps,
} from '../UI/Button/button.interface';
import { Headline1, Headline2, Headline3, Headline4 } from '../UI/Headline';
import { Text } from '../UI/Text';

import styles from './empty-state.module.scss';

import { CheckmarkRound } from 'views/components/UI/Icons';

const HEADINGS = { 1: Headline1, 2: Headline2, 3: Headline3, 4: Headline4 };
const CARD_HEADING_LEVEL = 3;
const PAGE_HEADING_LEVEL = 2;
type EmptyStateVariant = 'page' | 'card';

type EmptyStateAction =
  | (Omit<NativeButtonProps, 'children'> & { label: ReactNode })
  | (Omit<ButtonLinkProps, 'children'> & { label: ReactNode });

interface IEmptyStateProps {
  title: ReactNode;
  description: ReactNode;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  variant?: EmptyStateVariant;
  tone?: 'neutral' | 'success';
  headingLevel?: keyof typeof HEADINGS;
  className?: string;
}

const EmptyState: FC<IEmptyStateProps> = props => {
  const {
    title,
    description,
    primaryAction,
    secondaryAction,
    variant = 'page',
    tone = 'neutral',
    headingLevel = variant === 'card' ? CARD_HEADING_LEVEL : PAGE_HEADING_LEVEL,
    className,
  } = props;
  const Heading = HEADINGS[headingLevel];

  const renderAction = (
    action: EmptyStateAction,
    defaultMode: NonNullable<IButtonProps['mode']>,
  ): ReactNode => {
    const { label, mode = defaultMode, ...buttonProps } = action;

    if (action.link !== undefined) {
      return (
        <Button
          mode={mode}
          {...(buttonProps as Omit<ButtonLinkProps, 'children' | 'mode'>)}
        >
          {label}
        </Button>
      );
    }

    return (
      <Button
        mode={mode}
        {...(buttonProps as Omit<NativeButtonProps, 'children' | 'mode'>)}
      >
        {label}
      </Button>
    );
  };

  return (
    <div className={classNames(styles.emptyState, styles[variant], className)}>
      <div className={styles.content}>
        {tone === 'success' && (
          <div className={styles.successIcon} aria-hidden="true">
            <CheckmarkRound size="32px" />
          </div>
        )}
        <Heading className={styles.title}>{title}</Heading>
        <Text
          variant={variant === 'card' ? 'bodySmall' : 'body'}
          className={styles.description}
          align="center"
        >
          {description}
        </Text>
      </div>

      {(primaryAction || secondaryAction) && (
        <div className={styles.actions}>
          {primaryAction && renderAction(primaryAction, 'primary')}
          {secondaryAction && renderAction(secondaryAction, 'secondary')}
        </div>
      )}
    </div>
  );
};

export { EmptyState };
